<script setup lang="ts">
  import { ref, watch, inject, computed, onMounted } from 'vue';
  import { useRepairFinder } from 'src/composables/repairFinder';

  import RepairFullMapView from './RepairFullMapView.vue';
  import MapSideBar from './MapSideBar.vue';
  import RepairCardList from './RepairCardList.vue';
  import ConfirmationModal from './ConfirmationModal.vue';
  import WInput from 'src/components/base/Input.vue';

  import { useGoogleMaps } from 'src/composables/googleMaps';
  import { JsonFormsSubStates } from '@jsonforms/core';
  import { getLocationByIP, ILocationInfo } from 'src/utils/map';
  import { RepairLocation } from 'src/composables/repairFinder';
  import { debounce } from 'lodash';

  interface OverlayRepairFinderProps {
    editable?: boolean;
    data?: RepairLocation;
  }

  const props = defineProps<OverlayRepairFinderProps>();

  const emit = defineEmits<{
    save: [value: RepairLocation];
    close: [];
  }>();

  const jsonforms = inject<JsonFormsSubStates>('jsonforms');
  const incidentLocation = computed(() => jsonforms?.core?.data?.location as undefined | ILocationInfo);

  const mapId = 'repair-locations-full-map';
  const repairMapRef = ref<HTMLDivElement>();
  const initialData = computed(() => props.data);
  const {
    repairLocations,
    searchRepairLocations,
    setRequiredFilters,
    setMarkers,
    updateRepairLocations,
    isLoading,
    mapRef,
    requiredFilters,
    markerDetails,
  } = useRepairFinder({
    location: initialData,
    accidentLocation: incidentLocation,
  });
  updateRepairLocations([]);

  const { load, getLocation, loadPromise, placeChanged } = useGoogleMaps(repairMapRef, onMapMoved);

  const isFullPageView = ref(false);
  const isSameAsIncidentLocation = ref(false);
  const isDataInitialized = ref(false);

  const confirmModal = ref<{
    isOpen: boolean;
    title: string;
    text?: string;
    buttonText: string;
    action: () => void;
  }>({
    isOpen: false,
    title: '',
    text: '',
    buttonText: '',
    action: () => {},
  });

  const isProgrammaticMove = ref(false);

  function isSameLocation(
    loc1?: { latitude?: number; longitude?: number },
    loc2?: { latitude?: number; longitude?: number }
  ) {
    if (!loc1?.latitude || !loc1?.longitude || !loc2?.latitude || !loc2?.longitude) return false;
    return Math.abs(loc1.latitude - loc2.latitude) < 0.01 && Math.abs(loc1.longitude - loc2.longitude) < 0.01;
  }

  watch(incidentLocation, async (newLocation) => {
    if (!newLocation?.latitude || !newLocation?.longitude) return;
    if (isSameLocation(requiredFilters.value, newLocation)) return;

    isLoading.value = true;
    try {
      const filters = {
        latitude: Number(newLocation.latitude),
        longitude: Number(newLocation.longitude),
        radius: 500000,
      };
      if (!isNaN(filters.latitude) && !isNaN(filters.longitude)) {
        isProgrammaticMove.value = true;
        setRequiredFilters(filters);
        await searchRepairLocations();
        if (mapRef.value) {
          await setMarkers();
          mapRef.value.panTo({
            lat: filters.latitude,
            lng: filters.longitude,
          });
        }
        isProgrammaticMove.value = false;
      }
    } finally {
      isLoading.value = false;
    }
  });

  watch(repairMapRef, async () => {
    if (!repairMapRef.value) return;
    const start = new Date().getTime();
    try {
      isLoading.value = true;
      isDataInitialized.value = false;
      await loadPromise;

      const defaultLocation = props.data?.latitude
        ? { latitude: props.data.latitude, longitude: props.data.longitude }
        : incidentLocation.value?.latitude && incidentLocation.value?.longitude
          ? { latitude: incidentLocation.value.latitude, longitude: incidentLocation.value.longitude }
          : await getLocation();

      // Check if we already loaded locations for this position
      const map = await load(
        {
          zoom: 8,
          center: new google.maps.LatLng(defaultLocation.latitude, defaultLocation.longitude),
        },
        { marker: false }
      );
      mapRef.value = map;
      if (!isSameLocation(requiredFilters.value, defaultLocation)) {
        const filters = {
          latitude: Number(defaultLocation.latitude),
          longitude: Number(defaultLocation.longitude),
          radius: 50000,
        };
        setRequiredFilters(filters);
        await searchRepairLocations();
      }
      await setMarkers();

      isDataInitialized.value = true;
    } finally {
      const end = new Date().getTime();
      const difference = end - start;
      const timeout = difference < 1000 ? 1000 - difference : 0;
      setTimeout(() => {
        isDataInitialized.value = true;
        isLoading.value = false;
      }, timeout);
    }
  });

  async function onSearch(place: ILocationInfo, fromMap = false) {
    if (!place.latitude || !place.longitude) return;
    isLoading.value = true;
    try {
      if (!fromMap) {
        isProgrammaticMove.value = true;
        placeChanged(place, { zoom: 10 });
        isProgrammaticMove.value = false;
      }
      setRequiredFilters({
        latitude: place.latitude,
        longitude: place.longitude,
      });
      await searchRepairLocations();
      await setMarkers();
    } finally {
      isLoading.value = false;
    }
  }
  const debouncedSearch = debounce((place: ILocationInfo) => {
    onSearch(place, true);
  }, 1000);
  async function onMapMoved(place: ILocationInfo) {
    if (isProgrammaticMove.value) return;

    // don't trigger if center is within 0.3 degrees of the last center
    if (
      requiredFilters.value?.latitude &&
      requiredFilters.value?.longitude &&
      Math.abs(place.latitude - requiredFilters.value?.latitude) < 0.3 &&
      Math.abs(place.longitude - requiredFilters.value?.longitude) < 0.3
    ) {
      return;
    }
    debouncedSearch(place);
  }

  function closeOverlay(viewIndex: number) {
    if (viewIndex === 0) {
      isFullPageView.value = false;
      emit('close');
    } else if (viewIndex === 1) {
      searchRepairLocations();
    }
  }

  function onSaveData(payload: RepairLocation) {
    emit('save', payload);
  }

  const prevLocation = ref<{ latitude: number; longitude: number }>({ latitude: 0, longitude: 0 });
  async function setSameAsIncidentLocation(value: boolean | string) {
    if (value && !!incidentLocation.value?.latitude) {
      if (props.data?.latitude && props.data?.longitude) {
        prevLocation.value.latitude = props.data.latitude;
        prevLocation.value.longitude = props.data.longitude;
      }
      isProgrammaticMove.value = true;
      placeChanged(incidentLocation.value, { zoom: 10 });
      onSearch(incidentLocation.value);
      isProgrammaticMove.value = false;
    } else if (prevLocation.value) {
      isProgrammaticMove.value = true;
      placeChanged(prevLocation.value, { zoom: 10 });
      onSearch(prevLocation.value);
      isProgrammaticMove.value = false;
    }
  }

  watch(
    markerDetails,
    async () => {
      // deleteMarkers();
      // await setMarkers(false);
      if (!repairLocations.value?.length) return;
      if (!mapRef.value) return;
      const defaultLocation = markerDetails.value.find(({ is_active }) => is_active);
      if (defaultLocation?.latitude && defaultLocation?.longitude) {
        mapRef.value.panTo({
          lat: defaultLocation.latitude,
          lng: defaultLocation.longitude,
        });
      }
    },
    { deep: true }
  );
  onMounted(async () => {
    const defaultLocation = props.data?.latitude
      ? { latitude: props.data.latitude, longitude: props.data.longitude }
      : incidentLocation.value?.latitude && incidentLocation.value?.longitude
        ? { latitude: incidentLocation.value.latitude, longitude: incidentLocation.value.longitude }
        : await getLocationByIP().then(([res]) => res);

    setRequiredFilters({ latitude: defaultLocation?.latitude, longitude: defaultLocation?.longitude, radius: 50000 });
    await searchRepairLocations();
  });
  const isOpen = defineModel<boolean>({ default: false });
</script>

<template>
  <RepairFullMapView v-model="isOpen" :is-data-initialized="isDataInitialized" map-id="repair-locations-full-map">
    <template #sideBar>
      <MapSideBar :loading="!isDataInitialized" is-searchable @close="closeOverlay(0)" @change="onSearch">
        <!-- feature disabled as by default we show near incident location if possible -->
        <template v-if="false">
          <div class="widget-base-checkbox mb-4" v-if="isDataInitialized">
            <fieldset>
              <legend class="sr-only">{{ 'Same as incident location' }}</legend>
              <div class="widget-base-checkbox_checkbox">
                <div class="flex h-6 items-center">
                  <WInput
                    v-model="isSameAsIncidentLocation"
                    name="checkboxcontrol"
                    id="overlay-same-as-checkbox"
                    type="checkbox"
                    class="widget-base-checkbox_input"
                    @update:model-value="setSameAsIncidentLocation"
                    @keydown.enter="
                      ((isSameAsIncidentLocation = !isSameAsIncidentLocation),
                      setSameAsIncidentLocation(isSameAsIncidentLocation))
                    "
                  />
                </div>
                <div
                  class="ml-3 cursor-pointer text-sm leading-6"
                  @click="
                    () => (
                      (isSameAsIncidentLocation = !isSameAsIncidentLocation),
                      setSameAsIncidentLocation(isSameAsIncidentLocation)
                    )
                  "
                >
                  <label for="checkboxcontrol" class="widget-base-checkbox_label cursor-pointer">{{
                    'Near incident location'
                  }}</label>
                </div>
              </div>
            </fieldset>
          </div>
          <div v-else class="mb-4 flex h-6 w-full animate-pulse rounded bg-gray-200"></div>
        </template>
        <RepairCardList :loading="!isDataInitialized || isLoading" :repair-locations editable @save="onSaveData" />
      </MapSideBar>
    </template>
    <template #map>
      <div ref="repairMapRef" class="h-[50vh] w-full md:h-screen" v-show="isDataInitialized" :id="mapId"></div>
    </template>
  </RepairFullMapView>

  <ConfirmationModal
    v-if="confirmModal.isOpen"
    v-model="confirmModal.isOpen"
    :title="confirmModal.title"
    :text="confirmModal.text"
    :button-text="confirmModal.buttonText"
    @close="confirmModal.isOpen = false"
    @confirm="confirmModal.action()"
  />
</template>
